/**
 * The one `popstate` listener that runs BEFORE the router's.
 *
 * `<BrowserRouter>` handles Back/Forward in its own `popstate` listener, added
 * when it mounts, and has no API to hold a step back (`useBlocker` needs a data
 * router). A page that wants to ask before Back/Forward leaves it therefore
 * needs a listener the browser calls first — and listeners on `window` run in
 * the order they were added: Chrome does NOT run a capture listener on `window`
 * ahead of an earlier non-capture one (measured, Chromium 149). So this module
 * adds its listener at start-up, from `main.tsx`, before React renders the
 * router, and pages plug a guard into it.
 *
 * A guard returning true has taken the event: the router never sees it.
 */
export type PopStateGuard = (event: PopStateEvent) => boolean;

let current: PopStateGuard | null = null;
let installed = false;

function onPopState(event: PopStateEvent): void {
  if (current?.(event)) event.stopImmediatePropagation();
}

/** Adds the listener. Call once, before the router mounts; later calls do nothing. */
export function installPopStateGuard(target: Window = window): void {
  if (installed) return;
  installed = true;
  target.addEventListener("popstate", onPopState);
}

/** Whether the listener is in place — without it a guard would never be asked. */
export function popStateGuardInstalled(): boolean {
  return installed;
}

/** Makes `guard` the one asked about each Back/Forward step; returns its removal. */
export function setPopStateGuard(guard: PopStateGuard): () => void {
  current = guard;
  return () => {
    if (current === guard) current = null;
  };
}
